import { Link } from 'react-router-dom';
import { Plus, Calendar, User, HelpCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface QuickAction {
  label: string;
  description?: string;
  icon: React.ReactNode;
  href: string;
  color: 'primary' | 'secondary' | 'purple' | 'warning';
}

export interface QuickActionsProps {
  actions?: QuickAction[];
  columns?: 2 | 3 | 4;
  className?: string;
}

const defaultActions: QuickAction[] = [
  {
    label: 'Book Appointment',
    description: 'Browse services',
    icon: <Plus className="w-5 h-5" />,
    href: '/services',
    color: 'primary',
  },
  {
    label: 'View Bookings',
    description: 'Manage appointments',
    icon: <Calendar className="w-5 h-5" />,
    href: '/dashboard?section=bookings',
    color: 'secondary',
  },
  {
    label: 'My Profile',
    description: 'Account settings',
    icon: <User className="w-5 h-5" />,
    href: '/dashboard?section=profile',
    color: 'secondary',
  },
  {
    label: 'Get Help',
    description: 'Support center',
    icon: <HelpCircle className="w-5 h-5" />,
    href: '/dashboard?section=support',
    color: 'secondary',
  },
];

const colorClasses = {
  primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 group-hover:bg-primary-200 dark:group-hover:bg-primary-900/50',
  secondary: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 group-hover:bg-gray-200 dark:group-hover:bg-slate-600',
  purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50',
  warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 group-hover:bg-yellow-200 dark:group-hover:bg-yellow-900/50',
};

const columnClasses = {
  2: 'grid-cols-2',
  3: 'grid-cols-2 md:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4',
};

export function QuickActions({
  actions = defaultActions,
  columns = 4,
  className,
}: QuickActionsProps) {
  return (
    <div className={cn('grid gap-4', columnClasses[columns], className)}>
      {actions.map((action) => (
        <Link
          key={action.href}
          to={action.href}
          className="group bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md dark:hover:shadow-slate-900/50 transition-all text-center"
        >
          <div
            className={cn(
              'mx-auto mb-3 w-12 h-12 rounded-full flex items-center justify-center transition-colors',
              colorClasses[action.color]
            )}
          >
            {action.icon}
          </div>
          <p className="text-sm font-semibold text-gray-900 dark:text-slate-100 mb-1">{action.label}</p>
          {action.description && <p className="text-xs text-gray-500 dark:text-slate-400">{action.description}</p>}
        </Link>
      ))}
    </div>
  );
}
